import { Box, Button, Grid, Typography } from "@mui/material";
import { FC, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { Content } from "~/components/content/Content";
import WithSave from "~/controller/WithSave";
import { InfoTypeInput, InfoTypeResponseDto } from "~/gen/api/securefile";
import { OdResourcePageProps } from "~/types/resource";
import { useSubdataAssembleController, SubdataAssembleContextProvider, AssembleIssue } from "~/components/input/useSubdataAssembleController";
import InfoTypeEditor from "~/components/securefiles/InfoTypeEditor";
import { RecPatch } from "~/dataProvider/tools";
import Edit from "~/controller/Edit";
import { useDeleteWithConfirmController, useEditContext } from "ra-core";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import Confirm from "~/components/widgets/Confirm";
import usePermitGuard from "~/authProvider/usePermitGuard";
import { MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES } from "~/constants";


const baseInfoTypeRes = 'secure-files-base-info-type'


const EditInfoType: FC<OdResourcePageProps&RecPatch> = (props) => {
  const { id, resource } = props
  const handlers = useDefaultSavedHandlers("Edit Info Type")
  usePermitGuard([[MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES]])
  return (
    <Edit resource={resource} basePath="" id={id} mutationMode="pessimistic" {...handlers}>
      <EditInfoTypeBody {...props} />
    </Edit>
  )
}

const EditInfoTypeBody: FC<OdResourcePageProps&RecPatch> = ({
  id, options, resource,
}) => {
  const { record, refetch } = useEditContext<InfoTypeResponseDto&RecPatch>()
  const dispatch = useDispatch()

  const { assemble, contextValue } = useSubdataAssembleController<{d:InfoTypeInput}>({ 
    getInformed: (key, value) => {
      // will not get informed
    }
  })
  const handleSave = (save: (record:any,callbacks?:any)=>void) => async () => {
    const result = await assemble()
    
    if (result instanceof AssembleIssue) { return }
    const payload = result.d!

    save(payload, {
      onSuccess: () => {
        refetch && refetch()
      }
    })
  }

  const delHandlers = useDefaultSavedHandlers('Delete File Info Type')
  delHandlers.onSuccess.merge(useCallback(() => { dispatch(push(`/${resource}`)) }, [dispatch, resource]))
  const {
    open: deleteDialogOpen,
    loading,
    handleDialogOpen,
    handleDialogClose,
    handleDelete,
  } = useDeleteWithConfirmController({
    resource,
    record: { id },
    mutationMode: 'pessimistic',
    ...delHandlers,
  })

  return (
    <Content 
      pageName="Edit File Info Type" 
      resourceName="Edit" 
      breadcrumbs={[...(options?.breadcrumbs ?? []),
        { label: 'File Info Types', path: `/${resource}` }
      ]}
      mainActions={
        <>
          {!record?.isBuiltIn && 
            <Button sx={{ mr: theme => `${theme.spacing(4)} !important`}}
              color="error"
              variant="outlined"
              onClick={handleDialogOpen}
            >
              Delete
            </Button>
          }
          <WithSave 
            render={({save, saving}) =>
              <Button
                color="primary"
                disabled={saving}
                variant="contained"
                onClick={handleSave(save)}
              >
                Save
              </Button>}
          />
          <Button
            color="primary"
            variant="outlined"
            component={RouterLink}
            to={`/${resource}`}
          >
            Cancel
          </Button>
        </>
      }
    >
      <Grid container spacing={4} sx={{ mt: 2 }} >
        <Grid item xs={12}>
          <SubdataAssembleContextProvider value={contextValue as any}>
            <InfoTypeEditor title="Edit File Info Type" />
          </SubdataAssembleContextProvider>
        </Grid>
      </Grid>
      <Confirm
        isOpen={deleteDialogOpen}
        title={"Delete File Info Type"}
        loading={loading}
        onConfirm={handleDelete}
        onClose={handleDialogClose}
        confirm="Confirm"
        cancel="Cancel"
        content={(
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1 }}
              >
                Please confirm whether to proceed with the deletion of this
                {(record?.name) ? ` "${record?.name}" ` : " "}
                File Info Type.
              </Typography>
            </Box>
          </Box>
        )}
      />
    </Content>
  )
}

export default EditInfoType;

